import React, { useState, useEffect } from 'react';
import { Search, Eye, Download, Upload, FileText, Trash2, CheckCircle, XCircle, X } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api, { consultantsAPI } from '@/api/api';
import { readFileSmart } from '@/utils/imageCompressor';

const ConsultantsDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [formData, setFormData] = useState({
    entity_id: '',
    entity_name: '',
    title: '',
    document_type: 'general',
    file_name: '',
    file_data: '',
    file_type: '',
    file_size: 0,
    description: ''
  });

  useEffect(() => {
    fetchDocuments();
    fetchConsultants();
  }, []);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await api.get('/transcripts/consultants');
      setDocuments(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConsultants = async () => {
    try {
      const response = await consultantsAPI.getConsultants();
      setConsultants(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching consultants:', error);
    }
  };

  const processSelectedFile = async (file, originalName, originalSize, mimeType) => {
    if (originalSize > 5 * 1024 * 1024) { alert('File size must be less than 5MB'); return; }
    try {
      const result = await readFileSmart(file);
      setFormData(prev => ({
        ...prev,
        file_name: originalName,
        file_data: result.data,
        file_type: result.type || (mimeType.split('/')[1] || 'pdf'),
        file_size: result.size
      }));
      if (result.compressed) {
        console.log(`Image compressed: ${(originalSize/1024).toFixed(0)}KB -> ${(result.size/1024).toFixed(0)}KB`);
      }
    } catch (err) {
      console.error('File read failed:', err);
      alert('Failed to read file. Please try again.');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    processSelectedFile(file, file.name, file.size, file.type || 'application/octet-stream');
  };

  const handleConsultantSelect = (id) => {
    const consultant = consultants.find(c => c.id === id);
    if (consultant) {
      setFormData({
        ...formData,
        entity_id: consultant.id,
        entity_name: consultant.name || consultant.full_name
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.file_data) {
      alert('Please select a file to upload');
      return;
    }
    try {
      await api.post('/transcripts/documents', {
        ...formData,
        entity_type: 'consultant'
      });
      alert('Document uploaded successfully!');
      setShowModal(false);
      resetForm();
      fetchDocuments();
    } catch (error) {
      alert('Failed to upload document');
    }
  };

  const resetForm = () => {
    setFormData({
      entity_id: '',
      entity_name: '',
      title: '',
      document_type: 'general',
      file_name: '',
      file_data: '',
      file_type: '',
      file_size: 0,
      description: ''
    });
  };

  const handleView = async (docId) => {
    try {
      const response = await api.get(`/transcripts/documents/${docId}`);
      setSelectedDoc(response.data);
      setShowViewModal(true);
    } catch (error) {
      alert('Failed to load document');
    }
  };

  const handleVerify = async (docId, status) => {
    try {
      await api.put(`/transcripts/documents/${docId}/verify?status=${status}`);
      alert(`Document ${status.toLowerCase()} successfully!`);
      fetchDocuments();
    } catch (error) {
      alert('Failed to update document status');
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      await api.delete(`/transcripts/documents/${docId}`);
      fetchDocuments();
    } catch (error) {
      alert('Failed to delete document');
    }
  };

  const filteredDocuments = documents.filter(doc =>
    doc.entity_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const openModal = () => {
    setShowModal(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Consultants Documents</h1>
          <p className="text-sm text-gray-500 mt-1">Manage and view consultant document records</p>
        </div>
        <Button onClick={openModal} className="flex items-center gap-2">
          <Upload className="h-4 w-4" />
          Upload Document
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <Input
              type="text"
              placeholder="Search by consultant name, document title..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Consultant Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Document Title</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Upload Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr><td colSpan="6" className="px-4 py-8 text-center text-gray-500">Loading...</td></tr>
                ) : filteredDocuments.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-12 text-center text-gray-500">
                      <FileText className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                      No documents found. Click "Upload Document" to add a new document.
                    </td>
                  </tr>
                ) : (
                  filteredDocuments.map((doc) => (
                    <tr key={doc.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium">{doc.entity_name}</td>
                      <td className="px-4 py-3 text-sm">{doc.title}</td>
                      <td className="px-4 py-3 text-sm capitalize">{doc.document_type}</td>
                      <td className="px-4 py-3 text-sm">{doc.created_at?.split('T')[0]}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          doc.status === 'Verified' ? 'bg-green-100 text-green-800' :
                          doc.status === 'Rejected' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {doc.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button onClick={() => handleView(doc.id)} className="text-blue-600 hover:text-blue-800" title="View">
                            <Eye className="h-4 w-4" />
                          </button>
                          {doc.status === 'Pending' && (
                            <>
                              <button onClick={() => handleVerify(doc.id, 'Verified')} className="text-green-600 hover:text-green-800" title="Verify">
                                <CheckCircle className="h-4 w-4" />
                              </button>
                              <button onClick={() => handleVerify(doc.id, 'Rejected')} className="text-orange-600 hover:text-orange-800" title="Reject">
                                <XCircle className="h-4 w-4" />
                              </button>
                            </>
                          )}
                          <button onClick={() => handleDelete(doc.id)} className="text-red-600 hover:text-red-800" title="Delete">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Upload Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Upload Consultant Document</h2>
              <button onClick={() => setShowModal(false)}><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Select Consultant *</Label>
                <Select value={formData.entity_id} onValueChange={handleConsultantSelect}>
                  <SelectTrigger><SelectValue placeholder="Select a consultant" /></SelectTrigger>
                  <SelectContent>
                    {consultants.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name || c.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Document Title *</Label>
                <Input
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="e.g., Agreement, ID Proof"
                />
              </div>
              <div>
                <Label>Document Type</Label>
                <Select value={formData.document_type} onValueChange={(v) => setFormData({...formData, document_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="id_proof">ID Proof</SelectItem>
                    <SelectItem value="agreement">Agreement</SelectItem>
                    <SelectItem value="certificate">Certificate</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Upload File *</Label>
                <Input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileChange} />
                <p className="text-xs text-gray-500 mt-1">PDF, JPG, PNG (max 5MB)</p>
                {formData.file_name && <p className="text-sm text-green-600 mt-1">Selected: {formData.file_name}</p>}
              </div>
              <div>
                <Label>Description</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Optional description"
                />
              </div>
              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">Cancel</Button>
                <Button type="submit" className="flex-1">Upload</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedDoc && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Document Details</h2>
              <button onClick={() => setShowViewModal(false)}><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-gray-500">Consultant Name</Label><p className="font-medium">{selectedDoc.entity_name}</p></div>
                <div><Label className="text-gray-500">Document Title</Label><p className="font-medium">{selectedDoc.title}</p></div>
                <div><Label className="text-gray-500">Type</Label><p className="font-medium capitalize">{selectedDoc.document_type}</p></div>
                <div><Label className="text-gray-500">Status</Label><p className="font-medium">{selectedDoc.status}</p></div>
              </div>
              {selectedDoc.file_data && (
                <div className="mt-4">
                  <Label className="text-gray-500">Document Preview</Label>
                  {selectedDoc.file_type === 'pdf' ? (
                    <iframe src={selectedDoc.file_data} className="w-full h-96 border rounded mt-2" title="Document" />
                  ) : (
                    <img src={selectedDoc.file_data} alt="Document" className="max-w-full h-auto border rounded mt-2" />
                  )}
                </div>
              )}
              <div className="flex gap-2 pt-4">
                {selectedDoc.file_data && (
                  <a href={selectedDoc.file_data} download={selectedDoc.file_name} className="flex-1">
                    <Button variant="outline" className="w-full"><Download className="h-4 w-4 mr-2" />Download</Button>
                  </a>
                )}
                <Button onClick={() => setShowViewModal(false)} className="flex-1">Close</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsultantsDocuments;
